import { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Star, Quote, Users, TrendingUp, Loader2 } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StarRating({ rating, size = "w-4 h-4" }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${size} ${
            star <= rating
              ? "fill-amber-400 text-amber-400"
              : "fill-slate-600 text-slate-600"
          }`}
        />
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  const date = new Date(review.created_at).toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "short"
  });

  return (
    <Card className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-medium text-white">{review.client_name}</p>
            <p className="text-xs text-slate-500">{date}</p>
          </div>
          <StarRating rating={review.rating} />
        </div>
        {review.comment && (
          <div className="relative">
            <Quote className="absolute -top-1 -left-1 w-6 h-6 text-cyan-500/20" />
            <p className="text-slate-300 text-sm pl-4 italic">"{review.comment}"</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
      <Icon className={`w-6 h-6 mx-auto mb-2 ${color}`} />
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs text-slate-400">{label}</p>
    </div>
  );
}

function RatingBar({ stars, count, total }) {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-slate-400 w-3">{stars}</span>
      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
      <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-slate-500 w-8 text-right">{count}</span>
    </div>
  );
}

export default function ReviewsShowcasePage() {
  const [data, setData] = useState({ reviews: [], stats: null });
  const [shopName, setShopName] = useState("AMCPhone Martinique");
  const [loading, setLoading] = useState(true);

  
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/shop-info`)
      .then(r => r.json()).then(d => { if (d.shop_name) setShopName(d.shop_name); }).catch(() => {});
  }, []);

useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/reviews`);
        setData(res.data);
      } catch (err) {
        console.error("Error fetching reviews:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const { reviews, stats } = data;
  const avgRating = stats?.average_rating?.toFixed(1) || "0.0";

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Ce que nos clients disent de nous
          </h1>
          <p className="text-slate-400">
            {shopName} - Réparation de smartphones
          </p>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="col-span-2 md:col-span-1 bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-6 text-center">
            <div className="text-5xl font-bold text-amber-400 mb-1">{avgRating}</div>
            <StarRating rating={Math.round(parseFloat(avgRating))} size="w-5 h-5" />
            <p className="text-slate-400 text-sm mt-2">Note moyenne</p>
          </div>
          
          <StatCard 
            icon={Users} 
            label="Avis clients" 
            value={stats?.total_reviews || 0} 
            color="text-cyan-400" 
          />
          <StatCard 
            icon={Star} 
            label="5 étoiles" 
            value={stats?.five_star || 0} 
            color="text-amber-400" 
          />
          <StatCard 
            icon={TrendingUp} 
            label="Satisfaction" 
            value={stats?.total_reviews > 0 ? `${Math.round(((stats.five_star + stats.four_star) / stats.total_reviews) * 100)}%` : "0%"} 
            color="text-emerald-400" 
          />
        </div>

        {/* Rating Distribution */}
        <Card className="bg-slate-800/30 border-slate-700 mb-12">
          <CardContent className="p-6">
            <h3 className="text-lg font-medium text-white mb-4">Distribution des notes</h3>
            <div className="space-y-3">
              <RatingBar stars={5} count={stats?.five_star || 0} total={stats?.total_reviews || 0} />
              <RatingBar stars={4} count={stats?.four_star || 0} total={stats?.total_reviews || 0} />
              <RatingBar stars={3} count={stats?.three_star || 0} total={stats?.total_reviews || 0} />
              <RatingBar stars={2} count={stats?.two_star || 0} total={stats?.total_reviews || 0} />
              <RatingBar stars={1} count={stats?.one_star || 0} total={stats?.total_reviews || 0} />
            </div>
          </CardContent>
        </Card>

        {/* Reviews Grid */}
        {reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reviews.map((review, index) => (
              <ReviewCard key={index} review={review} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Star className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Aucun avis pour le moment</p>
            <p className="text-slate-500 text-sm">Soyez le premier à laisser un avis !</p>
          </div>
        )}

        {/* Footer CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 border border-cyan-500/20 rounded-2xl p-8">
          <h3 className="text-xl font-bold text-white mb-2">Besoin d'une réparation ?</h3>
          <p className="text-slate-400 mb-4">
            Faites confiance à nos experts pour réparer votre smartphone
          </p>
          <a 
            href="/intake" 
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white font-medium rounded-lg hover:from-cyan-600 hover:to-emerald-600 transition-colors"
          >
            Demander une prise en charge
          </a>
        </div>

        {/* Copyright */}
        <p className="text-center text-slate-500 text-sm mt-8">
          © 2024 {shopName}
        </p>
      </div>
    </div>
  );
}
